// The review lanes' task specs — the PR under review, and the reply pass over
// its threads. See `../task-envelope.ts` for the file docblock.

import { z } from 'zod';
import { DispatchableReviewModeSchema } from '../../entities/account';

/**
 * What made us review a pull request the repo defers to another bot on (#2321).
 *
 * SPELLED HERE, and this is the canonical spelling: the envelope schema is a
 * gate, and a gate cannot be written in terms of a package that depends on it —
 * `@developerz/review` depends on domain, never the reverse (the same constraint
 * that makes {@link ReviewTaskSpecSchema}'s `resolved_review` an opaque record).
 * `@developerz/review`'s `src/takeover.ts` RE-EXPORTS this enum rather than
 * spelling its own, so there is exactly one definition and nothing to drift; its
 * `REVIEW_TAKEOVER_SIGNAL_CAUSE` is a `Record<ReviewTakeoverSignal, string>`, so
 * a signal added here without a disclosure sentence fails to compile there.
 */
export const ReviewTakeoverSignal = {
  /** The incumbent declared it did not review this head (a rate-limit notice). */
  QuotaNotice: 'quota_notice',
  /** The incumbent is installed, CI is confirmed green, and it never arrived. */
  SilentPastGrace: 'silent_past_grace',
} as const;
export type ReviewTakeoverSignal = (typeof ReviewTakeoverSignal)[keyof typeof ReviewTakeoverSignal];
export const ReviewTakeoverSignalSchema = z.enum(ReviewTakeoverSignal);

/**
 * The takeover DISCLOSURE the box owes the maintainer, carried on the envelope.
 *
 * `vendor` is a bare string rather than `@developerz/review`'s
 * `ExternalReviewerVendor` for the dependency reason above; the box resolves it
 * to a display name through that package's own registry
 * (`renderTakeoverDisclosure`), which already falls back to the raw string for a
 * vendor it does not know.
 */
export const ReviewTakeoverSchema = z.object({
  vendor: z.string().min(1).max(64),
  signal: ReviewTakeoverSignalSchema,
});
export type ReviewTakeover = z.infer<typeof ReviewTakeoverSchema>;

/**
 * PR-review task context (slice 06, doc 06 "AI Review"). Present ONLY when
 * `origin === 'pr_review'`: the reviewer needs the concrete PR to fetch its diff
 * and anchor inline comments, while the generic envelope fields carry the
 * human-readable subject. `head_sha` pins the exact commit reviewed — it is the
 * idempotency key for the enqueue (a re-push mints a fresh review task) — and
 * `incremental` asks the runner to review only the commits since the last pass
 * (`review.auto_review.incremental` on a `synchronize`) rather than the whole diff.
 */
export const ReviewTaskSpecSchema = z.object({
  /**
   * The STRICT second review of an external PR's small-fix pass (#7785 part 2): the box runs
   * `pipeline/strict.ts` and files `review_strict_posted`, never `review_posted`. A box predating
   * this strips the key and files an ordinary review, which `findStrictReviewVerdict` does not read,
   * so an old box fails closed.
   */
  strict: z.object({ rules_version: z.number().int().positive() }).optional(),
  pr_number: z.number().int().positive(),
  head_sha: z.string().min(7).max(64),
  base_ref: z.string().min(1).max(255),
  incremental: z.boolean(),
  /**
   * True when the PR was authored by OUR GitHub App (`<app-slug>[bot]`) —
   * computed at enqueue from the webhook author (#838). GitHub 422s a review
   * app's APPROVE on its OWN PR (self-review), so the runner posts a real
   * `APPROVE` event only when this is false; an app-authored PR keeps the
   * quiet `COMMENT` and its approve verdict reaches the merge gate as the
   * recorded platform review-pass. Absent on envelopes minted before the
   * stamp — the runner reads that as "unknown" and does NOT approve (the
   * pre-#838 behaviour, never a self-review 422).
   */
  app_authored: z.boolean().optional(),
  /**
   * The owning ACCOUNT's entitled {@link ReviewMode} (#1523) — resolved from its
   * tier by the mothership at enqueue, so a box cannot widen what it was granted.
   *
   * Stamped here for the same reason `app_authored` is: it is a fact the box has
   * no way to establish and must not be able to override. `reviewEventFor`
   * (`@developerz/review`) is the enforcement point — only `full` may post a
   * merge-gating `APPROVE` / `REQUEST_CHANGES`; `comments` is advisory. Absent on
   * envelopes minted before the stamp, which reads as advisory (fail closed) —
   * never as `full`, which would gate a merge on an entitlement nobody checked.
   *
   * `off` is REFUSED by the schema, not merely unexpected: an `off` account gets
   * no review task at all, so an envelope carrying it would run a review the tier
   * does not include and only soften the posted event. A caller minting an
   * envelope directly cannot dispatch around the entitlement.
   */
  review_mode: DispatchableReviewModeSchema.optional(),
  /**
   * The platform-resolved `review:` config (#1483) — the mothership already
   * resolved + `PolicySchema`-validated the repo's `.maintainer.yml` (native wins)
   * or translated `.coderabbit.yaml` or fell to defaults at enqueue
   * (`loadEffectiveGate`), so the box acts on the SAME `Review` the enqueue gate
   * validated instead of re-reading a checkout path that does not exist on a
   * review box. The box prefers this via `resolveEffectiveReview(review, null)`.
   *
   * Carried as an opaque JSON object at the domain layer: the full `Review` shape
   * is owned by `@developerz/policy` `ReviewSchema`, and domain (which policy
   * depends on) cannot import it back. The producer (`reviewTaskEnvelope` in
   * `@developerz/review`) stamps a `ReviewSchema`-validated object; the consumer
   * (`apps/runner`) re-parses it with `ReviewSchema` to recover the typed shape.
   *
   * Optional + additive: a legacy envelope, or one minted by the external
   * `POST /v1/tasks` path from a caller-built body that omits it, still parses —
   * the box falls back to its on-box checkout read (the pre-#1483 path).
   *
   * Stays `z.record(z.string(), z.unknown())`: this is the OPAQUE transport of
   * a `ReviewSchema`-validated `Review` object (the producer — `reviewTaskEnvelope`
   * — always parses it; the consumer — `resolveEnvelopeReview` in `apps/runner` —
   * always re-parses it). A JSON-round-trip-safe schema would tighten this side
   * but mismatch the existing `Review` type's optional fields (e.g.
   * `tolerated_check_failures` is `string[] | undefined` by design, distinguishing
   * "absent" from "empty"); the run/state preview schema at `apps/runner/src/run-state.ts`
   * took the JSON-safe path because the run-state DATA is a ledger that must survive
   * a kill -9 unchanged. The PR-fix envelope is DIFFERENT and stays permissive.
   */
  resolved_review: z.record(z.string(), z.unknown()).optional(),
  /**
   * The GitHub issue the PR's coding task was minted from (#1909) — resolved at
   * enqueue via the marker two-hop (PR body `<!-- dz-task:… -->` →
   * tenant-fenced `tasks` read → `envelope.issue_number`), so the box can fetch
   * that issue on-box and review the diff against its stated acceptance
   * criteria (the completeness contract — the class of miss where a PR delivers
   * half its issue and reads as a clean diff, #1882). Absent on a PR with no
   * fleet marker, a task with no source issue, or an envelope minted before the
   * stamp — the box then reviews exactly as before (defects only). Optional by
   * design: a stale runner ignores it.
   */
  source_issue_number: z.number().int().positive().optional(),
  /**
   * This review only happened because the incumbent reviewer the repo defers to
   * could not review THIS head (#2321) — a quota notice, or silence past the
   * grace window. Stamped by the takeover mint
   * (`apps/worker/src/review/deference-recheck.ts`), which already holds both
   * values where it writes the `review.takeover` audit row.
   *
   * IT IS THE DISCLOSURE CHANNEL, and that is why it is on the envelope rather
   * than left to the box to infer. Bot-always-discloses is a non-negotiable, and
   * the box has no other way to learn it stood in for another reviewer: the
   * takeover decision is made in the mothership, and `ReviewTaskSpecSchema` is a
   * non-strict `z.object`, so before this field an unknown `takeover` key was
   * silently STRIPPED and the maintainer got an unexplained second bot review.
   *
   * OPTIONAL, and absent means an ORDINARY review — never "unknown". No envelope
   * minted before this field can be a takeover (the lane did not exist), so the
   * absent case needs no backfill and no migration, and the box's fallback is
   * the correct one: post no takeover disclosure.
   */
  takeover: ReviewTakeoverSchema.optional(),
  /**
   * Only the automatic babysit `stale_head` ask, not a deliberate same-head
   * re-review (degraded, rearmed, own-hold or rescue). Historical babysit
   * envelopes lack this evidence; never infer it from their problem prose.
   */
  stale_head_recovery: z.literal(true).optional(),
});
export type ReviewTaskSpec = z.infer<typeof ReviewTaskSpecSchema>;

/**
 * PR-review-THREAD engagement context (#258). Present ONLY when
 * `origin === 'pr_review_reply'`: a human replied on one of our review threads,
 * so the runner sweeps the PR's unresolved review threads and engages. The
 * trigger is the PR, not one comment — the runner reconciles ALL open threads
 * in one pass (idempotent: an already-resolved / already-answered thread is a
 * no-op), so a burst of replies coalesces instead of racing per-comment tasks.
 */
export const ReviewReplyTaskSpecSchema = z.object({
  pr_number: z.number().int().positive(),
  /** The login that triggered this pass — audit context; the sweep re-reads live threads. */
  triggered_by: z.string().min(1).max(255),
  /**
   * The platform-resolved `review:` config (#1483) — the twin of
   * {@link ReviewTaskSpecSchema.resolved_review} for the reply lane. The sweep's
   * consent gate (`chat_auto_reply`) and learnings knob ride the same precedence
   * the review handler honours, and carrying the resolved config here means a box
   * with no checkout still acts on the repo's own `.maintainer.yml`. Same opaque
   * transport shape + same optional/additive contract. See
   * {@link ReviewTaskSpecSchema.resolved_review} for the layering rationale.
   */
  resolved_review: z.record(z.string(), z.unknown()).optional(),
});
export type ReviewReplyTaskSpec = z.infer<typeof ReviewReplyTaskSpecSchema>;
